import { FunctionExt } from '../../util';
import { grid } from '../../layout/grid';
import { Node } from '../../model/node';
import { Model } from '../../model/model';
import { View } from '../../view/view';
import { Graph } from '../../graph/graph';
import { Dnd } from '../dnd';
export class Stencil extends View {
    constructor(options) {
        super();
        this.graphs = {};
        this.$groups = {};
        this.options = Object.assign(Object.assign({}, Stencil.defaultOptions), options);
        this.dnd = new Dnd(this.options);
        this.onSearch = FunctionExt.debounce(this.onSearch, 200);
        this.container = document.createElement('div');
        this.$container = this.$(this.container).addClass(this.prefixClassName(ClassNames.base));
        if (options.collapsable) {
            this.$container.addClass('collapsable').append(this.renderToggleAll());
        }
        if (options.search) {
            this.$container.addClass('searchable').append(this.renderSearch());
        }
        this.$content = this.$('<div/>')
            .addClass(this.prefixClassName(ClassNames.content))
            .appendTo(this.$container);
        const globalGraphOptions = options.stencilGraphOptions || {};
        if (options.groups && options.groups.length) {
            options.groups.forEach((group) => {
                const $group = this.$('<div/>')
                    .addClass(this.prefixClassName(ClassNames.group))
                    .attr('data-name', group.name)
                    .toggleClass('closed', group.closed === true);
                const $title = this.$('<h3/>')
                    .addClass(this.prefixClassName(ClassNames.groupTitle))
                    .html(group.label || group.name);
                const $content = this.$('<div/>').addClass(this.prefixClassName(ClassNames.groupContent));
                const graphOptionsInGroup = group.graphOptions;
                const graph = new Graph(Object.assign(Object.assign(Object.assign({}, globalGraphOptions), graphOptionsInGroup), { container: document.createElement('div'), model: globalGraphOptions.model || new Model(), width: group.width || options.width, height: group.height || options.height, interactive: false, preventDefaultBlankAction: false }));
                $content.append(graph.container);
                $group.append($title, $content).appendTo(this.$content);
                this.$groups[group.name] = $group;
                this.graphs[group.name] = graph;
            });
        }
        else {
            const graph = new Graph(Object.assign(Object.assign({}, globalGraphOptions), { container: document.createElement('div'), model: globalGraphOptions.model || new Model(), width: options.width, height: options.height, interactive: false, preventDefaultBlankAction: false }));
            this.$content.append(graph.container);
            this.graphs[Private.defaultGroupName] = graph;
        }
        this.startListening();
        return this;
    }
    get targetScroller() {
        const target = this.options.target;
        return target instanceof Graph ? target.scroller.widget : target;
    }
    get targetGraph() {
        const target = this.options.target;
        return target instanceof Graph ? target : target.graph;
    }
    get targetModel() {
        return this.targetGraph.model;
    }
    renderSearch() {
        return this.$('<div/>')
            .addClass(this.prefixClassName(ClassNames.search))
            .append(this.$('<input/>')
            .attr({
            type: 'search',
            placeholder: 'Search',
        })
            .addClass(this.prefixClassName(ClassNames.searchText)));
    }
    renderToggleAll() {
        return [
            this.$('<div/>')
                .addClass(this.prefixClassName(ClassNames.title))
                .append(this.$('<label/>').addClass('label').html(this.options.label))
                .append(this.$('<button/>').text('+').addClass('btn btn-expand'))
                .append(this.$('<button/>').text('-').addClass('btn btn-collapse')),
        ];
    }
    startListening() {
        const searchText = this.prefixClassName(ClassNames.searchText);
        const title = this.prefixClassName(ClassNames.title);
        const groupTitle = this.prefixClassName(ClassNames.groupTitle);
        this.delegateEvents({
            'click .btn-expand': 'expandGroups',
            'click .btn-collapse': 'collapseGroups',
            [`click .${title} > .label`]: 'expandGroups',
            [`click .${groupTitle}`]: 'onGroupTitleClick',
            [`touchstart .${groupTitle}`]: 'onGroupTitleClick',
            [`input .${searchText}`]: 'onSearch',
            [`focusin .${searchText}`]: 'onSearchFocusIn',
            [`focusout .${searchText}`]: 'onSearchFocusOut',
        });
        Object.keys(this.graphs).forEach((groupName) => {
            const graph = this.graphs[groupName];
            graph.on('cell:mousedown', this.onDragStart, this);
        });
    }
    stopListening() {
        this.undelegateEvents();
        Object.keys(this.graphs).forEach((groupName) => {
            const graph = this.graphs[groupName];
            graph.off('cell:mousedown', this.onDragStart, this);
        });
    }
    load(data, groupName) {
        if (Array.isArray(data)) {
            this.loadGroup(data, groupName);
        }
        else {
            if (this.options.groups) {
                Object.keys(this.options.groups).forEach((groupName) => {
                    if (data[groupName]) {
                        this.loadGroup(data[groupName], groupName);
                    }
                });
            }
        }
        return this;
    }
    loadGroup(cells, groupName) {
        const model = this.getModel(groupName);
        if (model) {
            const nodes = cells.map((cell) => cell instanceof Node ? cell : Node.create(cell));
            model.resetCells(nodes);
        }
        const group = this.getGroup(groupName);
        let height = this.options.height;
        if (group && group.height != null) {
            height = group.height;
        }
        if (this.options.layout) {
            this.options.layout.call(this, model, group);
        }
        if (!height) {
            const graph = this.getGraph(groupName);
            graph.fitToContent({
                minWidth: graph.options.width,
                gridHeight: 1,
                padding: this.options.graphPadding || 10,
            });
        }
        return this;
    }
    onDragStart(args) {
        const { e, node } = args;
        this.dnd.start(node, e);
    }
    filter(keyworld, filter) {
        const found = Object.keys(this.graphs).reduce((memo, groupName) => {
            const graph = this.graphs[groupName];
            const name = groupName === Private.defaultGroupName ? null : groupName;
            const items = graph.model.getCells().filter((cell) => {
                let matched = false;
                matched =
                    typeof filter === 'function'
                        ? filter.call(this, cell, keyworld, name, this)
                        : this.isCellMatched(cell, keyworld, typeof filter === 'boolean' ? {} : filter, keyworld.toLowerCase() !== keyworld);
                const view = graph.renderer.findViewByCell(cell);
                if (view) {
                    view.$(view.container).toggleClass('unmatched', !matched);
                }
                return matched;
            });
            const found = items.length > 0;
            const options = this.options;
            const model = new Model();
            model.resetCells(items);
            if (options.layout) {
                options.layout.call(this, model, this.getGroup(groupName));
            }
            if (this.$groups[groupName]) {
                this.$groups[groupName].toggleClass('unmatched', !found);
            }
            graph.fitToContent({
                gridWidth: 1,
                gridHeight: 1,
                padding: options.graphPadding || 10,
            });
            return memo || found;
        }, false);
        this.$container.toggleClass('not-found', !found);
    }
    isCellMatched(cell, keyworld, filters, ignoreCase) {
        if (keyworld && filters) {
            return Object.keys(filters).some((shape) => {
                const paths = filters[shape];
                return (('*' === shape || cell.shape === shape) &&
                    paths.some((path) => {
                        let val = cell.getPropByPath(path);
                        if (val != null) {
                            val = `${val}`;
                            if (ignoreCase) {
                                val = val.toLowerCase();
                            }
                            return val.indexOf(keyworld) >= 0;
                        }
                    }));
            });
        }
        return true;
    }
    onSearch(evt) {
        this.filter(evt.target.value, this.options.search);
    }
    onSearchFocusIn() {
        this.$container.addClass('is-focused');
    }
    onSearchFocusOut() {
        this.$container.removeClass('is-focused');
    }
    onGroupTitleClick(evt) {
        const $group = this.$(evt.target).closest(`.${this.prefixClassName(ClassNames.group)}`);
        this.toggleGroup($group.attr('data-name') || '');
    }
    getModel(groupName) {
        const graph = this.getGraph(groupName);
        return graph ? graph.model : null;
    }
    getGraph(groupName) {
        return this.graphs[groupName || Private.defaultGroupName];
    }
    getGroup(groupName) {
        const groups = this.options.groups;
        if (groupName != null && groups && groups.length) {
            return groups.find((group) => group.name === groupName);
        }
        return null;
    }
    toggleGroup(groupName) {
        if (this.isGroupCollapsed(groupName)) {
            this.expandGroup(groupName);
        }
        else {
            this.collapseGroup(groupName);
        }
        return this;
    }
    collapseGroup(groupName) {
        const $group = this.$groups[groupName];
        if ($group && !this.isGroupCollapsed(groupName)) {
            this.trigger('group:collapse', { name: groupName });
            $group.addClass('collapsed');
        }
        return this;
    }
    expandGroup(groupName) {
        const $group = this.$groups[groupName];
        if ($group && this.isGroupCollapsed(groupName)) {
            this.trigger('group:expand', { name: groupName });
            $group.removeClass('collapsed');
        }
        return this;
    }
    isGroupCollapsed(groupName) {
        const $group = this.$groups[groupName];
        return $group && $group.hasClass('collapsed');
    }
    collapseGroups() {
        Object.keys(this.$groups).forEach((groupName) => this.collapseGroup(groupName));
        return this;
    }
    expandGroups() {
        Object.keys(this.$groups).forEach((groupName) => this.expandGroup(groupName));
        return this;
    }
    onRemove() {
        Object.keys(this.graphs).forEach((groupName) => {
            const graph = this.graphs[groupName];
            graph.view.remove();
            delete this.graphs[groupName];
        });
        this.dnd.remove();
        this.stopListening();
        this.undelegateDocumentEvents();
    }
}
(function (Stencil) {
    Stencil.defaultOptions = Object.assign({ width: 200, height: 800, label: 'Stencil', collapsable: false, layout(model, group) {
            const options = {
                columnWidth: this.options.width / 2 - 10,
                columns: 2,
                rowHeight: 80,
                resizeToFit: false,
                dx: 10,
                dy: 10,
            };
            grid(model, Object.assign(Object.assign(Object.assign({}, options), this.options.layoutOptions), (group ? group.layoutOptions : {})));
        } }, Dnd.defaults);
})(Stencil || (Stencil = {}));
var ClassNames;
(function (ClassNames) {
    ClassNames.base = 'widget-stencil';
    ClassNames.title = `${ClassNames.base}-title`;
    ClassNames.search = `${ClassNames.base}-search`;
    ClassNames.searchText = `${ClassNames.search}-text`;
    ClassNames.content = `${ClassNames.base}-content`;
    ClassNames.group = `${ClassNames.base}-group`;
    ClassNames.groupTitle = `${ClassNames.group}-title`;
    ClassNames.groupContent = `${ClassNames.group}-content`;
})(ClassNames || (ClassNames = {}));
var Private;
(function (Private) {
    Private.defaultGroupName = '__default__';
})(Private || (Private = {}));
//# sourceMappingURL=index.js.map